<!-- src/views/FacturaServiciosList.vue -->
<template>
  <div class="factura-servicios-container">
    <!-- Header -->
    <div class="header-section">
      <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 class="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-3">
            <Icon icon="mdi:file-invoice" class="text-blue-600 text-3xl" />
            Gestor de Facturas de Servicios
          </h1>
          <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Administra y genera facturas de servicios técnicos
          </p>
        </div>
        <div class="flex items-center gap-3">
          <span class="text-sm text-gray-500">
            <span class="font-semibold text-blue-600">{{ facturas.length }}</span> factura(s)
          </span>
          <button
            @click="cargarFacturas"
            class="p-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition"
            title="Recargar"
          >
            <Icon icon="mdi:reload" class="text-xl" :class="{ 'animate-spin': cargando }" />
          </button>
        </div>
      </div>
    </div>

    <!-- Barra de Acciones -->
    <div class="actions-section bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-4">
      <div class="flex flex-wrap items-center gap-3">
        <button
          @click="abrirModalCrear"
          class="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-sm hover:shadow-md flex items-center gap-2 text-sm font-medium"
        >
          <Icon icon="mdi:plus-circle" class="text-lg" />
          Nueva Factura
        </button>
        <button
          @click="exportarLista"
          class="px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all shadow-sm flex items-center gap-2 text-sm font-medium"
        >
          <Icon icon="mdi:file-excel" class="text-lg" />
          Exportar
        </button>
        <div class="flex-1"></div>
        <div class="relative">
          <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            v-model="filtroBusqueda"
            type="text"
            placeholder="Buscar factura..."
            class="pl-10 pr-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white w-full md:w-64 text-sm"
            @input="aplicarFiltros"
          />
        </div>
        <select
          v-model="filtroEstado"
          class="px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
          @change="aplicarFiltros"
        >
          <option value="">Todos los estados</option>
          <option value="pendiente">Pendiente</option>
          <option value="pagado">Pagado</option>
          <option value="anulado">Anulado</option>
        </select>
      </div>
    </div>

    <!-- Tabla de Facturas -->
    <div class="table-section bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
              <th class="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">N° Factura</th>
              <th class="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Cliente</th>
              <th class="px-4 py-3.5 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Items</th>
              <th class="px-4 py-3.5 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Total</th>
              <th class="px-4 py-3.5 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Fecha</th>
              <th class="px-4 py-3.5 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Estado</th>
              <th class="px-4 py-3.5 text-center text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-if="cargando">
              <td colspan="7" class="px-4 py-12 text-center">
                <Icon icon="mdi:loading" class="animate-spin text-3xl text-blue-600 inline-block" />
                <p class="mt-2 text-gray-500 text-sm">Cargando facturas...</p>
              </td>
            </tr>
            <tr v-else-if="facturasFiltradas.length === 0">
              <td colspan="7" class="px-4 py-12 text-center">
                <Icon icon="mdi:file-document-outline" class="text-5xl text-gray-300 block mx-auto mb-3" />
                <p class="text-gray-500 text-sm">No hay facturas guardadas</p>
                <p class="text-gray-400 text-xs mt-1">Haz clic en "Nueva Factura" para comenzar</p>
              </td>
            </tr>
            <tr v-for="factura in facturasPagina" :key="factura.id" class="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition">
              <td class="px-4 py-3.5">
                <span class="font-bold text-blue-600 dark:text-blue-400 text-sm">{{ factura.numeroFactura }}</span>
              </td>
              <td class="px-4 py-3.5">
                <div class="font-medium text-gray-800 dark:text-white text-sm">{{ factura.cliente }}</div>
                <div class="text-xs text-gray-500">{{ factura.telefono || 'Sin teléfono' }}</div>
              </td>
              <td class="px-4 py-3.5 text-center">
                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                  {{ factura.items ? factura.items.length : 0 }}
                </span>
              </td>
              <td class="px-4 py-3.5 text-right">
                <span class="font-bold text-green-600 dark:text-green-400 text-sm">${{ (factura.total || 0).toFixed(2) }}</span>
              </td>
              <td class="px-4 py-3.5 text-center text-sm text-gray-600 dark:text-gray-300">
                {{ formatearFecha(factura.fecha) }}
              </td>
              <td class="px-4 py-3.5 text-center">
                <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                      :class="getEstadoClaseTexto(factura.estado)">
                  <span class="w-1.5 h-1.5 rounded-full mr-1.5"
                        :class="factura.estado === 'pagado' ? 'bg-green-500' : factura.estado === 'anulado' ? 'bg-red-500' : 'bg-yellow-500'"></span>
                  {{ factura.estado || 'Pendiente' }}
                </span>
              </td>
              <td class="px-4 py-3.5">
                <div class="flex items-center justify-center gap-1">
                  <button
                    @click="verFactura(factura)"
                    class="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition"
                    title="Ver PDF"
                  >
                    <Icon icon="mdi:file-pdf-box" class="text-xl" />
                  </button>
                  <button
                    @click="editarFactura(factura)"
                    class="p-1.5 text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 rounded-lg transition"
                    title="Editar"
                  >
                    <Icon icon="mdi:pencil" class="text-xl" />
                  </button>
                  <button
                    @click="eliminarFactura(factura)"
                    class="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition"
                    title="Eliminar"
                  >
                    <Icon icon="mdi:delete" class="text-xl" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginación -->
      <div class="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
        <span class="text-sm text-gray-500">
          Mostrando <span class="font-medium">{{ facturasPagina.length }}</span> de 
          <span class="font-medium">{{ facturasFiltradas.length }}</span> facturas
        </span>
        <div class="flex items-center gap-2">
          <button
            @click="paginaAnterior"
            :disabled="paginaActual === 1"
            class="px-3 py-1.5 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition text-sm"
          >
            Anterior
          </button>
          <span class="px-3 py-1.5 text-sm">
            Pág {{ paginaActual }} / {{ totalPaginas }}
          </span>
          <button
            @click="paginaSiguiente"
            :disabled="paginaActual === totalPaginas"
            class="px-3 py-1.5 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition text-sm"
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- MODAL: Nueva Factura / Editar                -->
    <!-- ============================================ -->
    <div v-if="mostrarModalCrear" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-hidden">
        <!-- Header -->
        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50">
          <div>
            <h2 class="text-xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <Icon icon="mdi:file-invoice" class="text-blue-600 text-2xl" />
              {{ facturaEditando ? 'Editar Factura' : 'Nueva Factura' }}
            </h2>
            <p class="text-sm text-gray-500">
              {{ facturaEditando ? `Editando: ${facturaEditando.numeroFactura}` : 'Busca órdenes por número o agrega items manuales' }}
            </p>
          </div>
          <button @click="cerrarModalCrear" class="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition">
            <Icon icon="mdi:close" class="text-2xl" />
          </button>
        </div>

        <!-- Cuerpo del Modal -->
        <div class="p-6 overflow-y-auto max-h-[calc(95vh-140px)]">
          <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <!-- Columna Izquierda: Búsqueda de Órdenes y Cliente -->
            <div class="lg:col-span-1">
              <!-- BÚSQUEDA DE ÓRDENES POR NÚMERO -->
              <div class="mb-4">
                <h4 class="font-semibold text-gray-700 dark:text-gray-300 text-sm mb-2 flex items-center gap-2">
                  <Icon icon="mdi:clipboard-search" />
                  Buscar Orden por Número
                </h4>
                <div class="flex gap-2">
                  <input
                    v-model="busquedaOrdenNumero"
                    type="text"
                    placeholder="Ej: 6688, 6022..."
                    class="flex-1 px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                    @keyup.enter="buscarOrdenPorNumero"
                  />
                  <button
                    @click="buscarOrdenPorNumero"
                    class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm flex items-center gap-1"
                    :disabled="buscandoOrden"
                  >
                    <Icon icon="mdi:magnify" :class="{ 'animate-spin': buscandoOrden }" />
                    Buscar
                  </button>
                </div>

                <!-- Resultado de búsqueda con TODOS LOS DETALLES -->
                <div v-if="ordenEncontrada" class="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div class="flex items-start justify-between">
                    <div class="flex-1">
                      <div class="font-bold text-blue-600 text-lg">#{{ ordenEncontrada.numeroOrden || ordenEncontrada.NumeroOrden || ordenEncontrada.idRecepcionEquipo }}</div>
                      
                      <!-- Cliente -->
                      <div class="text-sm mt-1">
                        <span class="font-medium text-gray-700 dark:text-gray-300">Cliente:</span>
                        <span>{{ ordenEncontrada.cliente?.nombre || ordenEncontrada.nombreCliente || 'Sin cliente' }}</span>
                      </div>
                      <div class="text-sm">
                        <span class="font-medium text-gray-700 dark:text-gray-300">Teléfono:</span>
                        <span>{{ ordenEncontrada.cliente?.telefono || ordenEncontrada.telefonoCliente || 'N/A' }}</span>
                      </div>
                      <div class="text-sm">
                        <span class="font-medium text-gray-700 dark:text-gray-300">Documento:</span>
                        <span>{{ ordenEncontrada.cliente?.documento || ordenEncontrada.documentoCliente || 'N/A' }}</span>
                      </div>
                      <div class="text-sm">
                        <span class="font-medium text-gray-700 dark:text-gray-300">Dirección:</span>
                        <span>{{ ordenEncontrada.cliente?.direccion || ordenEncontrada.direccionCliente || 'N/A' }}</span>
                      </div>
                      <div class="text-sm">
                        <span class="font-medium text-gray-700 dark:text-gray-300">Técnico:</span>
                        <span>{{ ordenEncontrada.tecnicoAsignado?.nombre || ordenEncontrada.nombreTecnico || 'N/A' }}</span>
                      </div>
                      <div class="text-sm">
                        <span class="font-medium text-gray-700 dark:text-gray-300">Estado:</span>
                        <span class="px-2 py-0.5 rounded-full text-xs font-medium" :class="getEstadoClase(ordenEncontrada.idEstadoRecepcion)">
                          {{ ordenEncontrada.estadoRecepcion?.nombre || ordenEncontrada.nombreEstado || 'Pendiente' }}
                        </span>
                      </div>
                      <div class="text-sm">
                        <span class="font-medium text-gray-700 dark:text-gray-300">Total:</span>
                        <span class="font-bold text-green-600">${{ (ordenEncontrada.precioTotalUsd || ordenEncontrada.PrecioTotalUsd || 0).toFixed(2) }}</span>
                      </div>
                      
                      <!-- EQUIPO -->
                      <div class="text-sm mt-2 p-2 bg-gray-100 dark:bg-gray-700 rounded">
                        <span class="font-medium text-gray-700 dark:text-gray-300">Equipo:</span>
                        <span class="font-bold">{{ ordenEncontrada.equipo || ordenEncontrada.Equipo || ordenEncontrada.descripcion || 'N/A' }}</span>
                      </div>
                      
                      <!-- PROBLEMA -->
                      <div class="text-sm mt-1 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded">
                        <span class="font-medium text-gray-700 dark:text-gray-300">Problema Reportado:</span>
                        <span>{{ ordenEncontrada.problema || ordenEncontrada.Problema || 'N/A' }}</span>
                      </div>

                      <!-- DIAGNÓSTICO -->
                      <div v-if="ordenEncontrada.problemaEncontrado || ordenEncontrada.actividadesRealizadas" class="text-sm mt-1 p-2 bg-blue-50 dark:bg-blue-900/20 rounded">
                        <span class="font-medium text-gray-700 dark:text-gray-300">Diagnóstico/Solución:</span>
                        <span>{{ ordenEncontrada.problemaEncontrado || ordenEncontrada.actividadesRealizadas?.split('\n')[0] || 'N/A' }}</span>
                      </div>

                      <!-- SERVICIOS Y REPUESTOS -->
                      <div v-if="ordenEncontrada.recepcionEquipoDetalles || ordenEncontrada.detalles" class="mt-2">
                        <span class="font-medium text-gray-700 dark:text-gray-300 text-sm">Servicios y Repuestos:</span>
                        <div class="mt-1 space-y-1">
                          <div v-for="(detalle, idx) in obtenerDetallesOrden(ordenEncontrada)" :key="idx" 
                               class="text-xs p-2 bg-gray-50 dark:bg-gray-700 rounded flex justify-between items-center">
                            <div>
                              <span class="font-medium">{{ detalle.productoServicio?.nombre || detalle.servicio?.nombre || detalle.nombre || 'Servicio' }}</span>
                              <span v-if="detalle.cantidad > 1" class="text-gray-400 ml-1">x{{ detalle.cantidad }}</span>
                              <span v-if="detalle.observaciones" class="text-gray-400 ml-1">- {{ detalle.observaciones }}</span>
                            </div>
                            <span class="font-bold text-green-600">${{ (detalle.subtotalUsd || detalle.total || 0).toFixed(2) }}</span>
                          </div>
                        </div>
                        <div class="mt-1 text-xs font-bold text-right text-blue-600">
                          Total Servicios: ${{ calcularTotalDetalles(ordenEncontrada).toFixed(2) }}
                        </div>
                      </div>
                    </div>
                    <button
                      @click="agregarOrdenEncontrada"
                      class="ml-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs whitespace-nowrap"
                      :disabled="ordenYaAgregada"
                    >
                      <Icon icon="mdi:plus" class="inline mr-1" />
                      {{ ordenYaAgregada ? 'Ya agregada' : 'Agregar' }}
                    </button>
                  </div>
                </div>
                <div v-else-if="busquedaOrdenNumero && busquedaOrdenNumero.length > 0 && !buscandoOrden && !ordenEncontrada" class="mt-2 text-sm text-red-500">
                  <Icon icon="mdi:alert-circle" class="inline mr-1" />
                  No se encontró la orden #{{ busquedaOrdenNumero }}
                </div>
              </div>

              <!-- Datos del Cliente -->
              <div class="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h3 class="font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                  <Icon icon="mdi:account" /> Datos del Cliente
                </h3>

                <div class="space-y-3">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Buscar Cliente
                    </label>
                    <div class="relative">
                      <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        v-model="busquedaCliente"
                        type="text"
                        placeholder="Buscar cliente..."
                        class="w-full pl-10 pr-4 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                        @input="filtrarClientesLocal"
                      />
                    </div>
                    <div v-if="clientesFiltrados.length > 0" class="mt-1 max-h-32 overflow-y-auto border rounded-lg dark:border-gray-700">
                      <div
                        v-for="cliente in clientesFiltrados"
                        :key="cliente.idCliente || cliente.id"
                        class="px-3 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer text-sm border-b border-gray-100 dark:border-gray-700 last:border-0"
                        @click="seleccionarCliente(cliente)"
                      >
                        <div class="font-medium">{{ cliente.nombre }} {{ cliente.apellido }}</div>
                        <div class="text-xs text-gray-500">{{ cliente.documento }} • {{ cliente.telefono }}</div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Nombre del Cliente <span class="text-red-500">*</span>
                    </label>
                    <input
                      v-model="clienteManual.nombre"
                      type="text"
                      placeholder="Nombre completo"
                      class="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Teléfono
                    </label>
                    <input
                      v-model="clienteManual.telefono"
                      type="text"
                      placeholder="0412-1234567"
                      class="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Documento
                    </label>
                    <input
                      v-model="clienteManual.documento"
                      type="text"
                      placeholder="V-12345678"
                      class="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Dirección
                    </label>
                    <input
                      v-model="clienteManual.direccion"
                      type="text"
                      placeholder="Dirección del cliente"
                      class="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    />
                  </div>
                </div>
              </div>
            </div>

            <!-- Columna Central: Items de la Factura -->
            <div class="lg:col-span-2">
              <div class="flex items-center justify-between mb-3">
                <h3 class="font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <Icon icon="mdi:format-list-bulleted" /> Items de la Factura
                </h3>
                <button
                  @click="agregarItemManual"
                  class="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm flex items-center gap-1"
                >
                  <Icon icon="mdi:plus" />
                  Agregar Item Manual
                </button>
              </div>

              <!-- Lista de Items -->
              <div class="space-y-2 max-h-80 overflow-y-auto">
                <div v-for="(item, index) in itemsFactura" :key="index" 
                     class="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                  <div class="grid grid-cols-12 gap-2 items-start">
                    <div class="col-span-5">
                      <label class="block text-xs font-medium text-gray-600 dark:text-gray-400">Concepto</label>
                      <input
                        v-model="item.concepto"
                        type="text"
                        placeholder="Ej: Reparación de equipo"
                        class="w-full px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                      />
                    </div>
                    <div class="col-span-2">
                      <label class="block text-xs font-medium text-gray-600 dark:text-gray-400">Cant.</label>
                      <input
                        v-model.number="item.cantidad"
                        type="number"
                        min="1"
                        step="1"
                        class="w-full px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                        @input="calcularItemTotal(index)"
                      />
                    </div>
                    <div class="col-span-2">
                      <label class="block text-xs font-medium text-gray-600 dark:text-gray-400">Precio $</label>
                      <input
                        v-model.number="item.precio"
                        type="number"
                        step="0.01"
                        min="0"
                        class="w-full px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                        @input="calcularItemTotal(index)"
                      />
                    </div>
                    <div class="col-span-2">
                      <label class="block text-xs font-medium text-gray-600 dark:text-gray-400">Total $</label>
                      <input
                        :value="(item.cantidad * item.precio).toFixed(2)"
                        type="text"
                        class="w-full px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm font-bold text-blue-600"
                        readonly
                      />
                    </div>
                    <div class="col-span-1 text-right pt-4">
                      <button
                        @click="eliminarItem(index)"
                        class="text-red-500 hover:text-red-700 p-1"
                        title="Eliminar item"
                      >
                        <Icon icon="mdi:close-circle" class="text-xl" />
                      </button>
                    </div>
                  </div>
                  <div v-if="item.esOrden" class="mt-1 text-xs text-blue-600 flex items-center gap-1">
                    <Icon icon="mdi:clipboard-check" />
                    Orden #{{ item.idOrden }}
                    <span v-if="item.equipo" class="text-gray-400 ml-2">{{ item.equipo }}</span>
                    <span v-if="item.problema" class="text-gray-400 ml-1">- {{ item.problema.substring(0, 30) }}{{ item.problema.length > 30 ? '...' : '' }}</span>
                  </div>
                  <!-- Mostrar detalles de la orden -->
                  <div v-if="item.esOrden && item.detalles && item.detalles.length > 0" class="mt-1 ml-6 space-y-0.5">
                    <div v-for="(det, idx) in item.detalles" :key="idx" class="text-xs text-gray-500 flex justify-between">
                      <span>{{ det.productoServicio?.nombre || det.servicio?.nombre || det.nombre || 'Servicio' }}</span>
                      <span class="font-medium">${{ (det.subtotalUsd || det.total || 0).toFixed(2) }}</span>
                    </div>
                  </div>
                </div>

                <div v-if="itemsFactura.length === 0" class="text-center text-gray-500 py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                  <Icon icon="mdi:plus-circle-outline" class="text-4xl block mx-auto mb-2" />
                  <p class="text-sm">No hay items agregados</p>
                  <p class="text-xs">Busca una orden por número o agrega un item manual</p>
                </div>
              </div>

              <!-- Totales Generales -->
              <div class="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label class="block text-xs font-medium text-gray-600 dark:text-gray-400">Subtotal</label>
                    <div class="text-lg font-bold text-gray-800 dark:text-white">${{ subtotal.toFixed(2) }}</div>
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-600 dark:text-gray-400">Descuento</label>
                    <input
                      v-model.number="descuento"
                      type="number"
                      step="0.01"
                      min="0"
                      class="w-full px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                      @input="calcularTotalesGenerales"
                    />
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-600 dark:text-gray-400">IVA (%)</label>
                    <input
                      v-model.number="iva"
                      type="number"
                      step="0.01"
                      min="0"
                      class="w-full px-2 py-1 border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
                      @input="calcularTotalesGenerales"
                    />
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-600 dark:text-gray-400">TOTAL</label>
                    <div class="text-2xl font-bold text-blue-600">${{ totalGeneral.toFixed(2) }}</div>
                  </div>
                </div>
              </div>

              <!-- Observaciones y extras -->
              <div class="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Observaciones / Notas
                  </label>
                  <textarea
                    v-model="observaciones"
                    rows="2"
                    class="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white resize-none"
                    placeholder="Notas adicionales..."
                  ></textarea>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Método de Pago
                  </label>
                  <select
                    v-model="metodoPago"
                    class="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="">Seleccionar...</option>
                    <option value="Efectivo">Efectivo</option>
                    <option value="Transferencia">Transferencia</option>
                    <option value="Pago Móvil">Pago Móvil</option>
                    <option value="Tarjeta Débito">Tarjeta Débito</option>
                    <option value="Tarjeta Crédito">Tarjeta Crédito</option>
                    <option value="Zelle">Zelle</option>
                  </select>
                </div>
              </div>

              <div class="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Estado
                  </label>
                  <select
                    v-model="estadoFactura"
                    class="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="pendiente">Pendiente</option>
                    <option value="pagado">Pagado</option>
                    <option value="anulado">Anulado</option>
                  </select>
                </div>
                <div>
                  <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tasa de Cambio (Bs/$)
                  </label>
                  <input
                    v-model.number="tasaCambio"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Ej: 36.50"
                    class="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer del Modal -->
        <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-end gap-3 bg-gray-50 dark:bg-gray-800/50">
          <button
            @click="cerrarModalCrear"
            class="px-4 py-2.5 border rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition text-sm font-medium"
          >
            Cancelar
          </button>
          <button
            v-if="facturaEditando"
            @click="actualizarFactura"
            class="px-6 py-2.5 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-all shadow-sm flex items-center gap-2 text-sm font-medium"
            :disabled="!clienteManual.nombre || itemsFactura.length === 0"
          >
            <Icon icon="mdi:content-save" />
            Actualizar Factura
          </button>
          <button
            v-else
            @click="guardarFactura"
            class="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all shadow-sm flex items-center gap-2 text-sm font-medium"
            :disabled="!clienteManual.nombre || itemsFactura.length === 0"
          >
            <Icon icon="mdi:file-pdf-box" />
            Generar y Guardar PDF
          </button>
        </div>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- MODAL: Nuevo Cliente                        -->
    <!-- ============================================ -->
    <div v-if="mostrarModalNuevoCliente" class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full">
        <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 class="text-lg font-bold text-gray-800 dark:text-white">Nuevo Cliente</h3>
          <button @click="cerrarModalNuevoCliente" class="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <Icon icon="mdi:close" class="text-xl" />
          </button>
        </div>
        <div class="p-6">
          <div class="space-y-3">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo Documento</label>
              <select v-model="nuevoCliente.tipoDocumento" class="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white">
                <option value="cedula">Cédula</option>
                <option value="rif">RIF</option>
                <option value="pasaporte">Pasaporte</option>
                <option value="identificacion_extranjera">Identificación Extranjera</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Documento</label>
              <input v-model="nuevoCliente.documento" type="text" placeholder="V-12345678" class="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre</label>
              <input v-model="nuevoCliente.nombre" type="text" placeholder="Nombre del cliente" class="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Apellido</label>
              <input v-model="nuevoCliente.apellido" type="text" placeholder="Apellido del cliente" class="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Teléfono</label>
              <input v-model="nuevoCliente.telefono" type="text" placeholder="0412-1234567" class="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
              <input v-model="nuevoCliente.email" type="email" placeholder="cliente@email.com" class="w-full px-3 py-2 border rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            </div>
          </div>
        </div>
        <div class="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
          <button @click="cerrarModalNuevoCliente" class="px-4 py-2 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition">Cancelar</button>
          <button @click="guardarNuevoCliente" class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">Guardar Cliente</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { obtenerRecepciones, obtenerRecepcionPorId, obtenerRecepcionConRelaciones } from "@/api/recepcionEquipo";
import { obtenerClientes, crearCliente } from "@/api/clientes";
import { 
  abrirFacturaServicios, 
  generarNumeroFactura, 
  formatearFechaFactura,
  guardarFacturaPDF,
  obtenerFacturasGuardadas,
  eliminarFacturaPDF,
  actualizarFacturaPDF
} from "@/utils/facturaServiciosGenerator";

export default {
  name: 'FacturaServiciosList',
  components: { Icon },
  data() {
    return {
      // Facturas
      cargando: false,
      facturas: [],
      facturasFiltradas: [],
      filtroBusqueda: '',
      filtroEstado: '',
      paginaActual: 1,
      itemsPorPagina: 10,

      // Modal Principal
      mostrarModalCrear: false,
      facturaEditando: null,
      
      // Clientes
      busquedaCliente: '',
      clientesCargados: [],
      clientesFiltrados: [],
      cargandoClientes: false,
      
      // Datos del Cliente (manual)
      clienteManual: {
        idCliente: null,
        nombre: '',
        telefono: '',
        documento: '',
        direccion: ''
      },
      
      // Búsqueda de órdenes
      busquedaOrdenNumero: '',
      buscandoOrden: false,
      ordenEncontrada: null,
      
      // Items manuales
      itemsFactura: [],
      
      // Totales
      subtotal: 0,
      descuento: 0,
      iva: 16,
      totalGeneral: 0,
      observaciones: '',
      metodoPago: '',
      estadoFactura: 'pendiente',
      tasaCambio: 0,

      // Modal Nuevo Cliente
      mostrarModalNuevoCliente: false,
      nuevoCliente: {
        tipoDocumento: 'cedula',
        documento: '',
        nombre: '',
        apellido: '',
        telefono: '',
        email: ''
      }
    };
  },
  computed: {
    totalPaginas() {
      return Math.ceil(this.facturasFiltradas.length / this.itemsPorPagina) || 1;
    },
    facturasPagina() {
      const inicio = (this.paginaActual - 1) * this.itemsPorPagina;
      const fin = inicio + this.itemsPorPagina;
      return this.facturasFiltradas.slice(inicio, fin);
    },
    ordenYaAgregada() {
      if (!this.ordenEncontrada) return false;
      return this.itemsFactura.some(item => 
        item.esOrden && item.idOrden === this.ordenEncontrada.idRecepcionEquipo
      );
    }
  },
  mounted() {
    this.cargarFacturas();
    this.cargarListaClientes();
  },
  methods: {
    // ===== CARGAR DATOS =====
    cargarFacturas() {
      this.cargando = true;
      try {
        this.facturas = obtenerFacturasGuardadas();
        this.aplicarFiltros();
      } catch (error) {
        console.error('Error cargando facturas:', error);
        this.facturas = [];
        this.facturasFiltradas = [];
      } finally {
        this.cargando = false;
      }
    },

    // ===== CARGAR CLIENTES =====
    async cargarListaClientes() {
      this.cargandoClientes = true;
      try {
        const response = await obtenerClientes();
        if (response.success) {
          this.clientesCargados = response.data || [];
          this.clientesFiltrados = [...this.clientesCargados];
        }
      } catch (error) {
        console.error('Error cargando clientes:', error);
      } finally {
        this.cargandoClientes = false;
      }
    },

    // ===== FILTRAR CLIENTES =====
    filtrarClientesLocal() {
      const busqueda = this.busquedaCliente.toLowerCase().trim();
      if (!busqueda) {
        this.clientesFiltrados = [];
        return;
      }
      
      this.clientesFiltrados = this.clientesCargados.filter(cliente => {
        const nombre = (cliente.nombre || '').toLowerCase();
        const apellido = (cliente.apellido || '').toLowerCase();
        const documento = (cliente.documento || '').toLowerCase();
        const telefono = (cliente.telefono || '').toLowerCase();
        return nombre.includes(busqueda) ||
               apellido.includes(busqueda) ||
               documento.includes(busqueda) ||
               telefono.includes(busqueda);
      });
    },

    // ===== SELECCIONAR CLIENTE =====
    seleccionarCliente(cliente) {
      this.clienteManual = {
        idCliente: cliente.idCliente || cliente.id,
        nombre: (cliente.nombre || '') + ' ' + (cliente.apellido || ''),
        telefono: cliente.telefono || '',
        documento: cliente.documento || '',
        direccion: cliente.direccion || ''
      };
      this.busquedaCliente = '';
      this.clientesFiltrados = [];
    },

    // ===== OBTENER DETALLES DE LA ORDEN =====
    obtenerDetallesOrden(orden) {
      if (!orden) return [];
      return orden.recepcionEquipoDetalles || orden.detalles || [];
    },

    calcularTotalDetalles(orden) {
      const detalles = this.obtenerDetallesOrden(orden);
      return detalles.reduce((sum, d) => sum + (d.subtotalUsd || d.total || 0), 0);
    },

    // ===== BUSCAR ORDEN POR NÚMERO CON RELACIONES =====
    async buscarOrdenPorNumero() {
      const num = this.busquedaOrdenNumero.trim();
      if (!num) {
        alert('Ingresa un número de orden');
        return;
      }

      this.buscandoOrden = true;
      this.ordenEncontrada = null;

      try {
        // Primero intentar obtener con relaciones usando el ID
        const numId = parseInt(num);
        if (!isNaN(numId)) {
          console.log(`🔍 Buscando orden #${numId} con relaciones...`);
          
          // Intentar obtener con relaciones
          const response = await obtenerRecepcionConRelaciones(numId);
          
          if (response.success && response.data) {
            this.ordenEncontrada = response.data;
            console.log('✅ Orden encontrada con relaciones:', this.ordenEncontrada);
            
            // Verificar si tiene detalles
            if (!this.ordenEncontrada.recepcionEquipoDetalles || this.ordenEncontrada.recepcionEquipoDetalles.length === 0) {
              console.warn('⚠️ La orden no tiene detalles cargados');
            } else {
              console.log(`📋 ${this.ordenEncontrada.recepcionEquipoDetalles.length} detalles encontrados`);
            }
            
            this.asignarClienteDesdeOrden(this.ordenEncontrada);
            this.buscandoOrden = false;
            return;
          }
          
          // Si falla, intentar solo por ID
          console.log(`🔍 Intentando búsqueda simple para #${numId}...`);
          const responseSimple = await obtenerRecepcionPorId(numId);
          if (responseSimple.success && responseSimple.data) {
            this.ordenEncontrada = responseSimple.data;
            console.log('✅ Orden encontrada (simple):', this.ordenEncontrada);
            this.asignarClienteDesdeOrden(this.ordenEncontrada);
            this.buscandoOrden = false;
            return;
          }
        }

        // Si no se encontró por ID, buscar en todas las órdenes
        console.log('🔍 Buscando en todas las órdenes...');
        const todasResponse = await obtenerRecepciones();
        if (todasResponse.success) {
          const encontrada = todasResponse.data.find(o => {
            const numOrden = String(o.numeroOrden || o.NumeroOrden || o.idRecepcionEquipo);
            return numOrden === num;
          });
          
          if (encontrada) {
            // Intentar cargar con relaciones
            const relResponse = await obtenerRecepcionConRelaciones(encontrada.idRecepcionEquipo);
            this.ordenEncontrada = relResponse.success && relResponse.data ? relResponse.data : encontrada;
            console.log('✅ Orden encontrada en lista:', this.ordenEncontrada);
            this.asignarClienteDesdeOrden(this.ordenEncontrada);
          } else {
            this.ordenEncontrada = null;
            console.warn('❌ Orden no encontrada:', num);
          }
        }

      } catch (error) {
        console.error('Error buscando orden:', error);
        this.ordenEncontrada = null;
      } finally {
        this.buscandoOrden = false;
      }
    },

    // ===== ASIGNAR CLIENTE DESDE ORDEN =====
    asignarClienteDesdeOrden(orden) {
      if (!orden) return;
      
      const cliente = orden.cliente || orden.Cliente;
      if (cliente) {
        this.clienteManual = {
          idCliente: cliente.idCliente || cliente.IdCliente || cliente.id,
          nombre: (cliente.nombre || '') + ' ' + (cliente.apellido || ''),
          telefono: cliente.telefono || '',
          documento: cliente.documento || '',
          direccion: cliente.direccion || ''
        };
      } else if (orden.nombreCliente) {
        this.clienteManual.nombre = orden.nombreCliente;
        this.clienteManual.telefono = orden.telefonoCliente || '';
        this.clienteManual.documento = orden.documentoCliente || '';
        this.clienteManual.direccion = orden.direccionCliente || '';
      }
    },

    // ===== AGREGAR ORDEN ENCONTRADA CON TODOS LOS DETALLES =====
    agregarOrdenEncontrada() {
      if (!this.ordenEncontrada) return;
      if (this.ordenYaAgregada) {
        alert('Esta orden ya está agregada a la factura');
        return;
      }

      const orden = this.ordenEncontrada;
      const total = orden.precioTotalUsd || orden.PrecioTotalUsd || 0;
      const equipo = orden.equipo || orden.Equipo || orden.descripcion || '';
      const problema = orden.problema || orden.Problema || '';
      
      // Obtener todos los detalles
      const detalles = this.obtenerDetallesOrden(orden);
      
      // Crear concepto con todos los detalles
      let concepto = `Orden #${orden.numeroOrden || orden.NumeroOrden || orden.idRecepcionEquipo}`;
      if (equipo) concepto += ` - ${equipo}`;
      
      // Agregar item principal
      this.itemsFactura.push({
        concepto: concepto,
        cantidad: 1,
        precio: total,
        total: total,
        esOrden: true,
        idOrden: orden.idRecepcionEquipo,
        equipo: equipo,
        problema: problema,
        detalles: detalles,
        ordenData: orden
      });

      // También agregar cada detalle como item individual para mejor visibilidad
      detalles.forEach(det => {
        const nombreDetalle = det.productoServicio?.nombre || det.servicio?.nombre || det.nombre || 'Servicio';
        const precioDetalle = det.subtotalUsd || det.total || 0;
        if (precioDetalle > 0) {
          this.itemsFactura.push({
            concepto: `  └ ${nombreDetalle}${det.observaciones ? ` (${det.observaciones})` : ''}`,
            cantidad: det.cantidad || 1,
            precio: det.precioUnitarioUsd || det.precio || 0,
            total: precioDetalle,
            esOrden: false,
            esDetalle: true,
            idOrden: orden.idRecepcionEquipo
          });
        }
      });

      this.ordenEncontrada = null;
      this.busquedaOrdenNumero = '';
      this.calcularTotalesGenerales();
      
      // Mostrar mensaje con resumen
      alert(`✅ Orden #${orden.numeroOrden || orden.NumeroOrden || orden.idRecepcionEquipo} agregada\nEquipo: ${equipo || 'N/A'}\nTotal: $${total.toFixed(2)}\nDetalles: ${detalles.length} servicios/repuestos`);
    },

    // ===== ITEMS MANUALES =====
    agregarItemManual() {
      this.itemsFactura.push({
        concepto: '',
        cantidad: 1,
        precio: 0,
        total: 0,
        esOrden: false,
        idOrden: null,
        equipo: '',
        problema: '',
        detalles: [],
        ordenData: null
      });
      this.calcularTotalesGenerales();
    },

    eliminarItem(index) {
      // Si es un detalle de orden, mostrar advertencia
      if (this.itemsFactura[index].esDetalle) {
        if (!confirm('Este es un detalle de una orden. ¿Seguro que quieres eliminarlo?')) {
          return;
        }
      }
      this.itemsFactura.splice(index, 1);
      this.calcularTotalesGenerales();
    },

    calcularItemTotal(index) {
      const item = this.itemsFactura[index];
      item.total = (item.cantidad || 0) * (item.precio || 0);
      this.calcularTotalesGenerales();
    },

    calcularTotalesGenerales() {
      this.subtotal = this.itemsFactura.reduce((sum, item) => sum + (item.total || 0), 0);
      const base = this.subtotal - (this.descuento || 0);
      this.totalGeneral = base + (base * (this.iva || 0) / 100);
    },

    // ===== FILTROS FACTURAS =====
    aplicarFiltros() {
      let resultado = [...this.facturas];
      
      if (this.filtroBusqueda) {
        const busq = this.filtroBusqueda.toLowerCase();
        resultado = resultado.filter(f => 
          f.numeroFactura?.toLowerCase().includes(busq) ||
          f.cliente?.toLowerCase().includes(busq)
        );
      }
      
      if (this.filtroEstado) {
        resultado = resultado.filter(f => f.estado === this.filtroEstado);
      }
      
      this.facturasFiltradas = resultado;
      this.paginaActual = 1;
    },

    // ===== MODAL CREAR =====
    abrirModalCrear() {
      this.mostrarModalCrear = true;
      this.facturaEditando = null;
      
      this.clienteManual = {
        idCliente: null,
        nombre: '',
        telefono: '',
        documento: '',
        direccion: ''
      };
      this.itemsFactura = [];
      this.subtotal = 0;
      this.descuento = 0;
      this.iva = 16;
      this.totalGeneral = 0;
      this.observaciones = '';
      this.metodoPago = '';
      this.estadoFactura = 'pendiente';
      this.tasaCambio = 0;
      this.busquedaCliente = '';
      this.clientesFiltrados = [];
      this.busquedaOrdenNumero = '';
      this.ordenEncontrada = null;
      
      if (this.clientesCargados.length === 0) {
        this.cargarListaClientes();
      }
    },

    cerrarModalCrear() {
      this.mostrarModalCrear = false;
      this.facturaEditando = null;
    },

    // ===== EDITAR FACTURA =====
    editarFactura(factura) {
      this.facturaEditando = factura;
      this.mostrarModalCrear = true;
      
      this.clienteManual = {
        idCliente: factura.idCliente,
        nombre: factura.cliente,
        telefono: factura.telefono || '',
        documento: factura.documento || '',
        direccion: factura.direccion || ''
      };
      
      this.itemsFactura = factura.items ? [...factura.items] : [];
      
      this.subtotal = factura.subtotal || factura.total || 0;
      this.descuento = factura.descuento || 0;
      this.iva = factura.iva || 16;
      this.totalGeneral = factura.total || 0;
      this.observaciones = factura.observaciones || '';
      this.metodoPago = factura.metodoPago || '';
      this.estadoFactura = factura.estado || 'pendiente';
      this.tasaCambio = factura.tasaCambio || 0;
      
      this.busquedaCliente = '';
      this.clientesFiltrados = [];
      this.busquedaOrdenNumero = '';
      this.ordenEncontrada = null;
    },

    // ===== GUARDAR FACTURA =====
    guardarFactura() {
      if (!this.clienteManual.nombre || this.itemsFactura.length === 0) {
        alert('Completa el nombre del cliente y agrega al menos un item');
        return;
      }

      const numeroFactura = generarNumeroFactura('FAC');
      
      // Preparar items para guardar (solo los principales, no los detalles duplicados)
      const itemsGuardar = this.itemsFactura
        .filter(item => !item.esDetalle) // Solo items principales
        .map(item => ({ 
          ...item,
          ordenData: item.ordenData ? { 
            ...item.ordenData,
            // Incluir detalles en la orden
            recepcionEquipoDetalles: item.detalles || []
          } : null
        }));

      const datosFactura = {
        id: Date.now().toString(),
        numeroFactura: numeroFactura,
        idCliente: this.clienteManual.idCliente,
        cliente: this.clienteManual.nombre,
        telefono: this.clienteManual.telefono || '',
        documento: this.clienteManual.documento || '',
        direccion: this.clienteManual.direccion || '',
        items: itemsGuardar,
        fecha: new Date().toISOString(),
        subtotal: this.subtotal,
        descuento: this.descuento,
        iva: this.iva,
        total: this.totalGeneral,
        observaciones: this.observaciones,
        metodoPago: this.metodoPago,
        tasaCambio: this.tasaCambio,
        estado: this.estadoFactura,
        tipo: 'mixta'
      };

      const resultado = guardarFacturaPDF(datosFactura);
      
      if (resultado.success) {
        alert(`✅ Factura ${numeroFactura} guardada correctamente`);
        this.cargarFacturas();
        this.cerrarModalCrear();
      } else {
        alert(`❌ Error al guardar: ${resultado.error}`);
      }
    },

    // ===== ACTUALIZAR FACTURA =====
    actualizarFactura() {
      if (!this.facturaEditando) return;

      const itemsActualizar = this.itemsFactura
        .filter(item => !item.esDetalle)
        .map(item => ({ 
          ...item,
          ordenData: item.ordenData ? { 
            ...item.ordenData,
            recepcionEquipoDetalles: item.detalles || []
          } : null
        }));

      const datosActualizados = {
        ...this.facturaEditando,
        idCliente: this.clienteManual.idCliente,
        cliente: this.clienteManual.nombre,
        telefono: this.clienteManual.telefono || '',
        documento: this.clienteManual.documento || '',
        direccion: this.clienteManual.direccion || '',
        items: itemsActualizar,
        subtotal: this.subtotal,
        descuento: this.descuento,
        iva: this.iva,
        total: this.totalGeneral,
        observaciones: this.observaciones,
        metodoPago: this.metodoPago,
        tasaCambio: this.tasaCambio,
        estado: this.estadoFactura,
        fechaActualizacion: new Date().toISOString()
      };

      const resultado = actualizarFacturaPDF(this.facturaEditando.id, datosActualizados);
      
      if (resultado.success) {
        alert(`✅ Factura ${datosActualizados.numeroFactura} actualizada`);
        this.cargarFacturas();
        this.cerrarModalCrear();
      } else {
        alert(`❌ Error al actualizar: ${resultado.error}`);
      }
    },

    // ===== VER FACTURA =====
    verFactura(factura) {
      if (factura.ruta) {
        window.open(factura.ruta, '_blank');
      } else {
        // Preparar datos para el PDF con todos los detalles
        const items = factura.items || [];
        
        // Obtener todos los detalles de las órdenes
        let todosLosDetalles = [];
        items.forEach(item => {
          if (item.esOrden && item.ordenData) {
            const detalles = item.ordenData.recepcionEquipoDetalles || item.ordenData.detalles || [];
            todosLosDetalles = [...todosLosDetalles, ...detalles];
          }
        });

        const datos = {
          numeroFactura: factura.numeroFactura,
          cliente: factura.cliente,
          telefono: factura.telefono || '',
          documento: factura.documento || '',
          direccion: factura.direccion || '',
          items: items,
          detalles: todosLosDetalles,
          ordenes: items.filter(i => i.esOrden).map(i => i.ordenData),
          fechaFactura: formatearFechaFactura(new Date(factura.fecha)),
          subtotal: factura.subtotal || factura.total,
          descuento: factura.descuento || 0,
          iva: factura.iva || 16,
          total: factura.total,
          observaciones: factura.observaciones || '',
          metodoPago: factura.metodoPago || '',
          tasaCambio: factura.tasaCambio || 0,
          tecnico: factura.tecnico || 'N/D'
        };
        abrirFacturaServicios(datos);
      }
    },

    // ===== ELIMINAR FACTURA =====
    eliminarFactura(factura) {
      if (confirm(`¿Eliminar la factura ${factura.numeroFactura}?`)) {
        const resultado = eliminarFacturaPDF(factura.id);
        if (resultado.success) {
          alert(`🗑️ Factura ${factura.numeroFactura} eliminada`);
          this.cargarFacturas();
        } else {
          alert(`❌ Error al eliminar: ${resultado.error}`);
        }
      }
    },

    // ===== NUEVO CLIENTE =====
    abrirModalNuevoCliente() {
      this.mostrarModalNuevoCliente = true;
      this.nuevoCliente = {
        tipoDocumento: 'cedula',
        documento: '',
        nombre: '',
        apellido: '',
        telefono: '',
        email: ''
      };
    },

    cerrarModalNuevoCliente() {
      this.mostrarModalNuevoCliente = false;
    },

    async guardarNuevoCliente() {
      try {
        const response = await crearCliente(this.nuevoCliente);
        if (response.success) {
          alert('✅ Cliente creado exitosamente');
          this.cerrarModalNuevoCliente();
          await this.cargarListaClientes();
          this.seleccionarCliente(response.data);
        } else {
          alert(`❌ Error al crear cliente: ${response.error}`);
        }
      } catch (error) {
        alert(`❌ Error: ${error.message}`);
      }
    },

    // ===== EXPORTAR =====
    exportarLista() {
      if (this.facturasFiltradas.length === 0) {
        alert('No hay facturas para exportar');
        return;
      }
      
      try {
        const XLSX = require('xlsx');
        const data = this.facturasFiltradas.map((f, i) => ({
          '#': i + 1,
          'N° Factura': f.numeroFactura,
          'Cliente': f.cliente,
          'Teléfono': f.telefono || '',
          'Items': f.items?.length || 0,
          'Total': f.total || 0,
          'Fecha': this.formatearFecha(f.fecha),
          'Estado': f.estado || 'Pendiente',
          'Método Pago': f.metodoPago || '',
          'Observaciones': f.observaciones || ''
        }));
        
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Facturas');
        
        const fecha = new Date().toISOString().split('T')[0];
        XLSX.writeFile(wb, `facturas_${fecha}.xlsx`);
        
        alert('✅ Archivo exportado exitosamente');
      } catch (error) {
        alert(`❌ Error al exportar: ${error.message}`);
      }
    },

    // ===== PAGINACIÓN =====
    paginaAnterior() {
      if (this.paginaActual > 1) this.paginaActual--;
    },
    paginaSiguiente() {
      if (this.paginaActual < this.totalPaginas) this.paginaActual++;
    },

    // ===== UTILS =====
    formatearFecha(fecha) {
      if (!fecha) return 'N/A';
      try {
        return new Date(fecha).toLocaleDateString('es-VE', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch {
        return fecha;
      }
    },

    getEstadoClase(idEstado) {
      const clases = {
        1: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
        2: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
        3: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
        4: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
        5: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
        6: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300'
      };
      return clases[idEstado] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    },

    getEstadoClaseTexto(estado) {
      const clases = {
        'pendiente': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
        'pagado': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
        'anulado': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
      };
      return clases[estado] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  }
};
</script>

<style scoped>
.factura-servicios-container {
  padding: 20px;
  max-width: 1440px;
  margin: 0 auto;
}

::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.dark ::-webkit-scrollbar-track {
  background: #2d3748;
}

.dark ::-webkit-scrollbar-thumb {
  background: #4a5568;
}

@media (max-width: 768px) {
  .factura-servicios-container {
    padding: 12px;
  }
}

* {
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.dark .bg-gray-50 {
  background-color: #1a202c;
}

.dark .border-gray-200 {
  border-color: #2d3748;
}
</style>